import { Stack, TextField } from '@mui/material'
import {
  StoppageReasonResponseDto,
  StoppageReasonResponsible,
  StoppageReasonType,
} from '@operations/app/api'
import { StoppageReasonFormData } from '@operations/stores/StoppageReasonStore'
import { useTranslate } from '@tolgee/react'
import { MobileViewAutocomplete, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

const getDefaultValues = (): StoppageReasonFormData => {
  return {
    id: undefined,
    name: '',
    shortName: '',
    description: '',
    type: null,
    responsible: null,
  }
}

interface Props {
  stoppageReason?: StoppageReasonResponseDto
  formId: string
  onSubmit: (formData: StoppageReasonFormData) => Promise<void>
}

export const StoppageReasonForm = observer(({ stoppageReason, formId, onSubmit }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StoppageReasonFormData>({ defaultValues: getDefaultValues() })

  useEffect(() => {
    if (stoppageReason) {
      reset(stoppageReason)
    }
  }, [reset, stoppageReason])

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap={theme.customSpacing.m}>
        <Controller
          control={control}
          name='name'
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              required
              error={!!errors['name']}
              label={t('name', 'Name')}
              fullWidth
              helperText={errors['name'] ? t('fieldIsRequired', 'Field is required') : undefined}
              data-cy='stoppage-reason-name-input'
            />
          )}
        />

        <Controller
          control={control}
          name='shortName'
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              required
              error={!!errors['shortName']}
              label={t('shortName', 'Short Name')}
              fullWidth
              helperText={
                errors['shortName'] ? t('fieldIsRequired', 'Field is required') : undefined
              }
              data-cy='stoppage-reason-short-name-input'
            />
          )}
        />

        <Controller
          control={control}
          name='description'
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errors['description']}
              label={t('description', 'Description')}
              value={field.value}
              fullWidth
              onChange={event => {
                field.onChange(event.target.value)
              }}
            />
          )}
        />

        <Controller
          control={control}
          name='type'
          rules={{ required: true }}
          render={({ field }) => (
            <MobileViewAutocomplete
              {...field}
              options={Object.values(StoppageReasonType)}
              value={Object.values(StoppageReasonType).find(x => x === field.value) ?? null}
              getOptionLabel={option => t(option)}
              isOptionEqualToValue={(option, value) => t(option) === value}
              onChange={(_, value) => {
                field.onChange(value ?? null)
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  key={params.id}
                  label={t('type', 'Type')}
                  required
                  error={!!errors.type}
                  helperText={errors.type ? t('fieldIsRequired', 'Field is required') : undefined}
                  data-cy='stoppage-reason-type-autocomplete'
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name='responsible'
          rules={{ required: true }}
          render={({ field }) => (
            <MobileViewAutocomplete
              {...field}
              options={Object.values(StoppageReasonResponsible)}
              value={Object.values(StoppageReasonResponsible).find(x => x === field.value) ?? null}
              isOptionEqualToValue={(option, value) => t(option) === value}
              getOptionLabel={option => t(option)}
              onChange={(_, value) => {
                field.onChange(value ?? null)
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  key={params.id}
                  label={t('responsible', 'Responsible')}
                  required
                  error={!!errors.responsible}
                  helperText={
                    errors.responsible ? t('fieldIsRequired', 'Field is required') : undefined
                  }
                  data-cy='stoppage-reason-responsible-autocomplete'
                />
              )}
            />
          )}
        />
      </Stack>
    </form>
  )
})
